import {
  createAsyncThunk,
  createSlice,
  createEntityAdapter,
  createSelector
} from '@reduxjs/toolkit';

import { fetchAddCampaign } from 'api/campaignApi';
import config from 'config';
import { paymentTypes } from 'constants/campaignConstants';
import { campaignStatuses } from 'constants/statuses';
import showSnackbarError from 'utils/showSnackbarError';

import { openBookedDialog } from './bookedDialogSlice';
import { updateCampaign } from './campaignsSlice';

const kiosksAdapter = createEntityAdapter({ selectId: (kiosk) => kiosk.id });

const initialState = {
  id: null,
  name: '',
  description: '',
  type: null,
  billingId: null,
  companyProfileId: null,
  payment: {
    costTotal: 0,
    productionFee: 0,
    discount: 0,
    orderTotal: null,
    initialPaymentDue: 0,
    subtotal: 0,
    campaignDuration: config.defaultCampaignDuration,
    paymentType: config.defaultCampaignPaymentType,
    countOfPayments: null,
    discountType: null,
    discountNote: null
  },
  kiosks: kiosksAdapter.getInitialState(),
  isLoading: false
};

export const createNewCampaignDraft = createAsyncThunk(
  'newCampaign/createNewCampaignDraft',
  async (_, { getState, rejectWithValue, dispatch }) => {
    const { id, companyProfileId, name, type, kiosks } = getState().newCampaign;

    const kiosksArray = Object.values(kiosks.entities);

    try {
      const data = {
        corporate_profile_id: companyProfileId,
        name,
        workflow_id: type,
        kiosks: kiosksArray.map((kiosk) => ({
          id: kiosk.id,
          address: kiosk.address,
          image: kiosk.image,
          store_image: kiosk.storeImage,
          name: kiosk.name,
          placement_type: kiosk.placementType,
          price: kiosk.price,
          store_id: kiosk.storeId,
          location_in_venue: kiosk.locationInVenue,
          displays: kiosk.displays,
          type: kiosk.type,
          store_available_kiosks: kiosk.storeAvailableKiosks,
          venue_id: kiosk.venueId,
          venue_type: kiosk.venueType,
          import_kiosk_id: kiosk.importKioskId,
          number_of_digital_kiosks: kiosk.numberOfDigitalKiosks,
          kiosk_ids: kiosk.kioskIds,
          is_digital: kiosk.isDigital,
          duration_in_seconds: kiosk.adDuration,
          rank: kiosk.rank
        }))
      };

      if (id) {
        data.id = id;
      }

      const campaign = await fetchAddCampaign(data);

      dispatch(updateCampaign(campaign));

      return campaign.id;
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { kiosks, status } = getState().newCampaign;

      const kiosksArray = Object.values(kiosks.entities);

      if (kiosksArray.length === 0 || status === campaignStatuses.CAMPAIGN_CREATED) {
        return false;
      }

      return true;
    }
  }
);

export const createNewCampaign = createAsyncThunk(
  'newCampaign/createNewCampaign',
  async ({ campaignStartDate }, { getState, rejectWithValue, dispatch }) => {
    const { id, companyProfileId, name, type, payment, kiosks } = getState().newCampaign;
    const {
      productionFee,
      discount,
      orderTotal,
      initialPaymentDue,
      installmentPaymentAmount,
      subtotal,
      campaignDuration,
      countOfPayments,
      paymentType,
      costTotal,
      discountType,
      discountNote
    } = payment;

    const kiosksArray = Object.values(kiosks.entities);

    try {
      const data = {
        corporate_profile_id: companyProfileId,
        name,
        workflow_id: type,
        payment_plan: {
          production_fee: productionFee,
          discount,
          order_total: orderTotal,
          initial_payment_due: initialPaymentDue,
          installment_payment_amount: installmentPaymentAmount,
          subtotal,
          campaign_duration: campaignDuration,
          count_of_payments: countOfPayments,
          payment_plan_type: paymentType,
          subtotal_before_discount: costTotal,
          discount_type: discountType,
          discount_note: discountNote
        },
        kiosks: kiosksArray.map((kiosk) => ({
          id: kiosk.id,
          address: kiosk.address,
          image: kiosk.image,
          name: kiosk.name,
          placement_type: kiosk.placementType,
          price: kiosk.price,
          store_id: kiosk.storeId,
          location_in_venue: kiosk.locationInVenue,
          displays: kiosk.displays,
          type: kiosk.type,
          store_available_kiosks: kiosk.storeAvailableKiosks,
          venue_id: kiosk.venueId,
          venue_type: kiosk.venueType,
          import_kiosk_id: kiosk.importKioskId,
          number_of_digital_kiosks: kiosk.numberOfDigitalKiosks,
          kiosk_ids: kiosk.kioskIds,
          is_digital: kiosk.isDigital,
          duration_in_seconds: kiosk.adDuration,
          rank: kiosk.rank
        }))
      };

      if (id) {
        data.id = id;
      }

      if (campaignStartDate) {
        data.start_date = campaignStartDate;
      }

      const campaign = await fetchAddCampaign(data);

      dispatch(updateCampaign(campaign));

      return campaign.id;
    } catch (e) {
      if (e?.response?.data?.statusCode === 409 && e?.response?.data?.errors[0]?.booked_until) {
        dispatch(openBookedDialog({ bookedUntilDate: e.response.data.errors[0].booked_until }));
      } else {
        dispatch(showSnackbarError(e.message, e.stack));
      }

      return rejectWithValue(e.message);
    }
  }
);

export const newCampaignSlice = createSlice({
  name: 'newCampaign',
  initialState,
  reducers: {
    changeCampaignType(state, action) {
      state.type = action.payload.id;
    },
    changeCampaignName(state, { payload }) {
      state.name = payload.name;
      state.description = payload.description;
      state.companyProfileId = payload.companyProfileId;
      state.companyType = payload.companyType;
    },
    addKiosks(state, action) {
      kiosksAdapter.addMany(state.kiosks, action.payload);
    },
    removeKiosks(state, action) {
      kiosksAdapter.removeMany(state.kiosks, action.payload);
    },
    addKiosk(state, action) {
      kiosksAdapter.addOne(state.kiosks, action.payload);
    },
    removeKiosk(state, action) {
      kiosksAdapter.removeOne(state.kiosks, action.payload);
    },
    updateKiosk(state, action) {
      kiosksAdapter.updateOne(state.kiosks, action.payload);
    },
    clearNewCampaign(state) {
      state.id = null;
      state.type = null;
      state.name = '';
      state.description = '';
      state.isDraftCampaign = false;

      kiosksAdapter.removeAll(state.kiosks);
    },
    changeCampaignPayment(state, { payload }) {
      state.payment = { ...state.payment, ...payload };
    },
    fillDraftCampaign(state, { payload }) {
      state.id = payload.id;
      state.type = payload.type;
      state.name = payload.name;
      state.description = payload.description;
      state.companyProfileId = payload.companyProfileId;
      state.isDraftCampaign = true;

      kiosksAdapter.addMany(state.kiosks, payload.kiosks);
    },
    changeCampaignDuration(state, { payload }) {
      state.payment = { ...state.payment, campaignDuration: payload.campaignDuration };
    },
    changeCampaignPaymentType(state, { payload }) {
      if (payload.paymentType === paymentTypes.MONTHLY_SUBSCRIPTION) {
        state.payment = {
          ...state.payment,
          campaignDuration: 1,
          countOfPayments: 1,
          paymentType: payload.paymentType
        };
      } else if (payload.paymentType === paymentTypes.ANNUAL_SUBSCRIPTION) {
        state.payment = {
          ...state.payment,
          campaignDuration: 12,
          countOfPayments: 1,
          paymentType: payload.paymentType
        };
      } else {
        state.payment = {
          ...state.payment,
          campaignDuration: '',
          countOfPayments: '',
          paymentType: payload.paymentType
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(createNewCampaign.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createNewCampaign.fulfilled, (state) => {
      state.isLoading = false;
      state.status = campaignStatuses.CAMPAIGN_CREATED;
    });
    builder.addCase(createNewCampaign.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(createNewCampaignDraft.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createNewCampaignDraft.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(createNewCampaignDraft.rejected, (state) => {
      state.isLoading = false;
    });
  }
});

const { selectAll: selectAllKiosks } = kiosksAdapter.getSelectors(
  (state) => state.newCampaign.kiosks
);

const id = createSelector(
  (state) => state.newCampaign,
  (state) => state.id
);
const kiosks = createSelector(selectAllKiosks, (state) => state);

export const NewCampaignSelectors = { id, kiosks };

export const {
  changeCampaignType,
  changeCampaignName,
  changeCampaignPayment,
  changeCampaignDuration,
  changeCampaignPaymentType,
  addKiosks,
  removeKiosks,
  clearNewCampaign,
  addKiosk,
  removeKiosk,
  updateKiosk,
  fillDraftCampaign
} = newCampaignSlice.actions;

export default newCampaignSlice.reducer;
